import { Auth } from 'aws-amplify';
import { ApiResponse, User } from '../types';
import { UserType, isValidUserType, isHostLike, USER_TYPES } from '../constants/userTypes';
import { graphqlApi } from './graphql';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  storeOAuthState,
  retrieveOAuthState,
  clearOAuthState,
} from '../utils/oauth';
import { parseClaimsFromPayload, parseClaimsFromIdTokenString } from '../utils/tokenClaims';

/**
 * Map an AWS Amplify / Cognito error to a stable, language-neutral error code
 * that the UI layer can dispatch against `auth:authErrors.<CODE>` i18n keys.
 *
 * When Cognito returns an error, `error.code` is usually the exception name
 * (e.g. `NotAuthorizedException`). Some edge cases (e.g. network failures)
 * have no `code` — we fall back to `UNKNOWN` so the UI can render a generic
 * localized message instead of the raw English `error.message`.
 */
export function mapAuthErrorToCode(error: any): string {
  if (!error) return 'UNKNOWN';

  const code = typeof error.code === 'string' ? error.code : '';
  if (code) return code;

  // Some Amplify errors surface as `error.name` only (e.g. NetworkError).
  const name = typeof error.name === 'string' ? error.name : '';
  if (name) return name;

  return 'UNKNOWN';
}

// Direct authentication service that bypasses GraphQL and uses Amplify Auth directly
export const directAuth = {
  async signUp(
    email: string,
    password: string,
    name: string,
    userType: UserType,
    tosAcceptedVersion: string,
    tosAcceptedAt: number,
    marketingConsent?: boolean
  ): Promise<ApiResponse> {
    try {
      console.log('🚀 Starting direct sign up...', { email, name, userType, tosAcceptedVersion, marketingConsent });

      // Step 1: Create Cognito user with ToS and marketing consent data
      const result = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          name,
          profile: userType, // Save userType in profile field
          'custom:tosAcceptedVersion': tosAcceptedVersion,
          'custom:tosAcceptedAt': tosAcceptedAt.toString(),
          ...(marketingConsent !== undefined && {
            'custom:marketingConsent': marketingConsent.toString(),
          }),
        },
      });

      console.log('✅ Cognito signup successful:', result);

      return {
        success: true,
        message: 'Registration successful! Please check your email for verification code.',
        data: {
          userId: result.userSub,
          user: result,
          userType,
        },
      };
    } catch (error: any) {
      console.error('❌ SignUp error:', error);
      // Normalize Cognito's opaque "SignUp is not permitted for this user pool"
      // (thrown as NotAuthorizedException) into UsernameExistsException. This
      // message is what our user pool returns when a pre-signup trigger
      // blocks a duplicate-email registration, and treating it as wrong
      // credentials (the default mapping for NotAuthorizedException) would
      // show a misleading "Incorrect email or password" toast.
      const code = mapAuthErrorToCode(error);
      const msg = typeof error?.message === 'string' ? error.message : '';
      const isDuplicateEmailBlocked =
        code === 'NotAuthorizedException' && /SignUp is not permitted/i.test(msg);
      return {
        success: false,
        errorCode: isDuplicateEmailBlocked ? 'UsernameExistsException' : code,
        error: error.message || 'Registration failed',
      };
    }
  },

  async signIn(email: string, password: string): Promise<ApiResponse> {
    try {
      console.log('🚀 Starting direct sign in...', { email });
      let attributesMap: { [key: string]: string } = {};

      const result = await Auth.signIn({
        username: email,
        password,
      });

      console.log('✅ Sign in result:', result);

      if (result) {
        // Get current user and session
        const [currentUser, session] = await Promise.all([
          Auth.currentAuthenticatedUser(),
          Auth.currentSession()
        ]);

        console.log('👤 Current user:', currentUser);

        // Try to fetch user details from database to get the correct userType
        let userType: UserType = USER_TYPES.BRAND; // default (brand replaces legacy host)
        let userName = currentUser.signInDetails?.loginId || email.split('@')[0];

        // Get custom attributes directly from Cognito
        let userTypeFromCognito: string | undefined;
        try {
          const attributes = await Auth.userAttributes(currentUser);
          console.log('👤 Cognito attributes:', attributes);

          // Parse attributes array into key-value object
          if (Array.isArray(attributes)) {
            attributes.forEach((attr: any) => {
              if (attr.Name && attr.Value) {
                attributesMap[attr.Name] = attr.Value;
              }
            });
          }

          console.log('📋 Parsed attributes map:', attributesMap);

          // Get userType from Cognito profile field (primary source)
          userTypeFromCognito = attributesMap['profile'];
          if (userTypeFromCognito && isValidUserType(userTypeFromCognito)) {
            userType = userTypeFromCognito;
            console.log('✅ UserType from Cognito profile field:', userType);
          } else {
            console.log('⚠️ UserType not found in Cognito profile, using default:', userType);
          }

          // Get name from Cognito attributes
          if (attributesMap['name']) {
            userName = attributesMap['name'];
            console.log('✅ User name from Cognito:', userName);
          }
        } catch (attrError) {
          console.log('⚠️ Failed to fetch user attributes, trying DynamoDB fallback:', attrError);
        }

        const userId = currentUser.username || currentUser.sub || currentUser.userId;

        // Fetch profile ID (hostId or influencerId) based on userType
        let hostId: string | undefined;
        let influencerId: string | undefined;

        try {
          if (isHostLike(userType)) {
            console.log('🔍 Fetching host profile...');
            const hostProfileResponse = await graphqlApi.getHostProfileByUserId(userId);
            if (hostProfileResponse.success && hostProfileResponse.data) {
              hostId = hostProfileResponse.data.hostId;
              console.log('✅ Host profile found:', hostId);
            }
          } else if (userType === 'influencer') {
            console.log('🔍 Fetching influencer profile...');
            const influencerProfileResponse = await graphqlApi.getInfluencerByUserId(userId);
            if (influencerProfileResponse.success && influencerProfileResponse.data) {
              influencerId = influencerProfileResponse.data.influencerId;
              console.log('✅ Influencer profile found:', influencerId);
            }
          }
        } catch (profileError) {
          console.log('⚠️ Failed to fetch profile ID:', profileError);
          // Continue without profile ID - it will be fetched later if needed
        }

        const idTokenPayload = session.getIdToken().decodePayload() as Record<string, unknown>;
        const tokenClaims = parseClaimsFromPayload(idTokenPayload);

        return {
          success: true,
          message: 'Login successful!',
          data: {
            user: {
              userId,
              email: attributesMap['email'] || email,
              name: userName,
              userType: userType,
              hostId,
              influencerId,
              roles: tokenClaims.roles,
              permissions: tokenClaims.permissions,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            tokens: {
              accessToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
            },
            isSignedIn: !!result,
          },
        };
      } else {
        return {
          success: false,
          errorCode: 'SIGN_IN_NOT_COMPLETED',
          error: 'Sign in not completed',
          data: { result },
        };
      }
    } catch (error: any) {
      console.error('❌ SignIn error:', error);
      return {
        success: false,
        errorCode: mapAuthErrorToCode(error),
        error: error.message || 'Login failed',
      };
    }
  },

  async confirmEmail(email: string, confirmationCode: string): Promise<ApiResponse> {
    try {
      console.log('🚀 Starting email confirmation...', { email });

      const result = await Auth.confirmSignUp(email, confirmationCode);

      console.log('✅ Email confirmation result:', result);

      return {
        success: true,
        message: 'Email confirmed successfully! You can now sign in.',
        data: {
          result: result,
        },
      };
    } catch (error: any) {
      console.error('❌ ConfirmSignUp error:', error);
      return {
        success: false,
        errorCode: mapAuthErrorToCode(error),
        error: error.message || 'Email confirmation failed',
      };
    }
  },

  async resendVerificationCode(email: string): Promise<ApiResponse> {
    try {
      console.log('🚀 Resending verification code...', { email });

      await Auth.resendSignUp(email);

      console.log('✅ Verification code resent successfully');

      return {
        success: true,
        message: 'Verification code sent! Please check your email.',
      };
    } catch (error: any) {
      console.error('❌ ResendSignUp error:', error);
      return {
        success: false,
        errorCode: mapAuthErrorToCode(error),
        error: error.message || 'Failed to resend verification code',
      };
    }
  },

  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();

      let userType: UserType = USER_TYPES.BRAND; // default (brand replaces legacy host)
      let userName = currentUser.signInDetails?.loginId || '';
      let attributesMap: { [key: string]: string } = {};

      // 1) Read everything we can from the ID token first — always available,
      //    no extra Cognito scope required. This prevents a noisy 400 from
      //    Auth.userAttributes on OAuth sessions whose access token lacks
      //    `aws.cognito.signin.user.admin` scope.
      try {
        const session = await Auth.currentSession();
        const payload = session.getIdToken().decodePayload() as Record<string, any>;
        if (payload.email) attributesMap['email'] = payload.email;
        if (payload.name) attributesMap['name'] = payload.name;
        if (payload.profile && isValidUserType(payload.profile)) {
          userType = payload.profile;
        }
        userName = payload.name || payload.given_name || payload['cognito:username'] || userName;
      } catch (tokenError) {
        console.log('⚠️ Failed to read ID token claims:', tokenError);
      }

      // 2) Try to enrich with Auth.userAttributes. Skips silently when the
      //    access token lacks the admin scope (typical for OAuth).
      const hasAdminScope = (() => {
        try {
          const access = currentUser.signInUserSession?.accessToken?.payload;
          const scopes: string = access?.scope || '';
          return scopes.split(' ').includes('aws.cognito.signin.user.admin');
        } catch {
          return false;
        }
      })();

      if (hasAdminScope) {
        try {
          const attributes = await Auth.userAttributes(currentUser);
          console.log('👤 Current user Cognito attributes:', attributes);

          if (Array.isArray(attributes)) {
            attributes.forEach((attr: any) => {
              if (attr.Name && attr.Value) {
                attributesMap[attr.Name] = attr.Value;
              }
            });
          }

          console.log('📋 Current user parsed attributes map:', attributesMap);

          const userTypeFromProfile = attributesMap['profile'];
          if (userTypeFromProfile && isValidUserType(userTypeFromProfile)) {
            userType = userTypeFromProfile;
          }

          if (attributesMap['name']) {
            userName = attributesMap['name'];
          }
        } catch (attrError) {
          console.log('⚠️ Failed to fetch current user attributes:', attrError);
        }
      }

      const userId = currentUser.username || currentUser.sub || currentUser.userId;

      // Extract role/permission claims injected by Pre Token Generation Lambda
      let tokenClaims = { roles: [] as string[], permissions: [] as string[] };
      try {
        const session = await Auth.currentSession();
        const payload = session.getIdToken().decodePayload() as Record<string, unknown>;
        tokenClaims = parseClaimsFromPayload(payload);
      } catch (claimsError) {
        console.log('⚠️ Failed to read token claims:', claimsError);
      }

      // Fetch profile ID (hostId or influencerId) based on userType
      let hostId: string | undefined;
      let influencerId: string | undefined;

      try {
        if (isHostLike(userType)) {
          console.log('🔍 Fetching host profile for current user...');
          const hostProfileResponse = await graphqlApi.getHostProfileByUserId(userId);
          if (hostProfileResponse.success && hostProfileResponse.data) {
            hostId = hostProfileResponse.data.hostId;
            console.log('✅ Host profile found:', hostId);
          }
        } else if (userType === 'influencer') {
          console.log('🔍 Fetching influencer profile for current user...');
          const influencerProfileResponse = await graphqlApi.getInfluencerByUserId(userId);
          if (influencerProfileResponse.success && influencerProfileResponse.data) {
            influencerId = influencerProfileResponse.data.influencerId;
            console.log('✅ Influencer profile found:', influencerId);
          }
        }
      } catch (profileError) {
        console.log('⚠️ Failed to fetch profile ID for current user:', profileError);
        // Continue without profile ID - it will be fetched later if needed
      }

      const user: User = {
        userId,
        email: attributesMap['email'] || '',
        name: userName,
        userType: userType,
        hostId,
        influencerId,
        roles: tokenClaims.roles,
        permissions: tokenClaims.permissions,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return {
        success: true,
        data: user,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'No authenticated user',
      };
    }
  },

  async signOut(): Promise<ApiResponse> {
    try {
      await Auth.signOut();

      return {
        success: true,
        message: 'Signed out successfully',
      };
    } catch (error: any) {
      console.error('❌ SignOut error:', error);
      return {
        success: false,
        error: error.message || 'Sign out failed',
      };
    }
  },

  // Helper method to create user records in DynamoDB after Cognito signup
  async createUserRecords(): Promise<any> {
    try {
      // For now, we'll skip the backend call to avoid authentication issues
      // The user record will be created when they first login and we detect the missing data
      console.log('🔧 Backend record creation deferred until first login');
      return { success: true, deferred: true };

    } catch (error) {
      console.error('❌ Failed to create user records:', error);
      throw error;
    }
  },

  /**
   * Initiates OAuth flow by redirecting directly to the provider (bypasses Cognito Hosted UI)
   * @param provider OAuth provider (google, facebook, apple)
   * @param userType User type for sign-up flows
   * @param tosData Terms of Service acceptance data for sign-up flows
   * @param redirectPath Optional path to redirect to after authentication
   */
  async initiateOAuthFlow(
    provider: 'google' | 'facebook' | 'apple' | 'naver' | 'kakao',
    userType?: UserType,
    tosData?: {
      version: string;
      acceptedAt: string;
      marketingConsent: boolean;
    },
    redirectPath?: string
  ): Promise<void> {
    try {
      console.log('🚀 Initiating OAuth flow (direct to provider)...', { provider, userType });

      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
      const redirectUri = `${window.location.origin}/oauth/callback`;

      if (!cognitoDomain || !clientId) {
        throw new Error('OAuth configuration missing: REACT_APP_COGNITO_DOMAIN and REACT_APP_USER_POOL_CLIENT_ID are required');
      }

      const state = generateState();

      // Naver doesn't support OIDC — handle it directly without Cognito
      if (provider === 'naver') {
        // Store state with empty codeVerifier to identify Naver callback later
        storeOAuthState({
          userType,
          tosData,
          codeVerifier: '', // Empty = Naver flow
          state,
          redirectPath,
        });

        const naverClientId = process.env.REACT_APP_NAVER_CLIENT_ID;
        if (!naverClientId) {
          throw new Error('Naver OAuth not configured: REACT_APP_NAVER_CLIENT_ID missing in frontend env.');
        }
        const naverAuthorizeUrl = new URL('https://nid.naver.com/oauth2.0/authorize');
        naverAuthorizeUrl.searchParams.set('response_type', 'code');
        naverAuthorizeUrl.searchParams.set('client_id', naverClientId);
        naverAuthorizeUrl.searchParams.set('redirect_uri', redirectUri);
        naverAuthorizeUrl.searchParams.set('state', state);

        console.log('🔗 Redirecting directly to Naver:', naverAuthorizeUrl.toString());
        window.location.assign(naverAuthorizeUrl.toString());
        return;
      }

      // Generate PKCE code verifier and challenge for Cognito-based providers
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store OAuth state for after redirect
      storeOAuthState({
        userType,
        tosData,
        codeVerifier,
        state,
        redirectPath,
      });

      // For Cognito-supported providers (Google, Kakao, etc.)
      const identityProviderMap: Record<string, string> = {
        google: 'Google',
        facebook: 'Facebook',
        apple: 'SignInWithApple',
        kakao: 'Kakao',
      };

      const identityProvider = identityProviderMap[provider];

      // Build Cognito authorize URL with identity_provider param to skip Hosted UI
      const authorizeUrl = new URL(`https://${cognitoDomain}/oauth2/authorize`);
      authorizeUrl.searchParams.set('response_type', 'code');
      authorizeUrl.searchParams.set('client_id', clientId);
      authorizeUrl.searchParams.set('redirect_uri', redirectUri);
      authorizeUrl.searchParams.set('scope', 'email openid profile');
      authorizeUrl.searchParams.set('state', state);
      authorizeUrl.searchParams.set('code_challenge', codeChallenge);
      authorizeUrl.searchParams.set('code_challenge_method', 'S256');
      authorizeUrl.searchParams.set('identity_provider', identityProvider);

      console.log('🔗 Redirecting directly to provider via:', authorizeUrl.toString());

      // Redirect directly to provider (skips Cognito Hosted UI)
      window.location.assign(authorizeUrl.toString());
    } catch (error: any) {
      console.error('❌ OAuth flow initiation failed:', error);
      throw error;
    }
  },

  /**
   * Handles OAuth callback - exchanges authorization code for tokens using PKCE
   * and creates an authenticated session
   */
  async handleOAuthCallback(code: string, state: string): Promise<ApiResponse> {
    try {
      console.log('🚀 Handling OAuth callback...', { code: code.substring(0, 10) + '...' });

      // Retrieve stored OAuth state
      const oauthState = retrieveOAuthState();
      if (!oauthState) {
        return {
          success: false,
          error: 'OAuth session expired. Please try again.',
        };
      }

      // Validate state parameter to prevent CSRF
      if (state && oauthState.state && state !== oauthState.state) {
        clearOAuthState();
        return {
          success: false,
          error: 'Invalid authentication state. Please try again.',
        };
      }

      // Check if this is a Naver callback (Naver doesn't go through Cognito)
      // We detect this by checking if codeVerifier is empty or if the state indicates Naver
      // Since Naver redirects directly, the code is a Naver auth code, not a Cognito auth code
      // We stored the provider info implicitly — if codeVerifier is empty, it's Naver
      if (!oauthState.codeVerifier) {
        return this.handleNaverCallback(code, oauthState);
      }

      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
      const redirectUri = `${window.location.origin}/oauth/callback`;

      if (!cognitoDomain || !clientId) {
        clearOAuthState();
        return {
          success: false,
          error: 'OAuth configuration missing',
        };
      }

      // Exchange authorization code for tokens using PKCE
      const tokenUrl = `https://${cognitoDomain}/oauth2/token`;
      const tokenParams = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: oauthState.codeVerifier,
      });

      console.log('🔄 Exchanging authorization code for tokens...');

      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: tokenParams.toString(),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        console.error('❌ Token exchange failed:', errorData);
        clearOAuthState();
        return {
          success: false,
          error: errorData.error_description || errorData.error || 'Failed to exchange authorization code',
        };
      }

      const tokens = await tokenResponse.json();
      console.log('✅ Tokens received');

      // Parse the ID token to get user info
      const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
      console.log('👤 ID token payload:', idTokenPayload);

      // Store tokens in localStorage for Amplify to pick up
      // Amplify v5 stores tokens under specific keys in localStorage
      const userPoolId = process.env.REACT_APP_USER_POOL_ID || '';
      const lastAuthUser = idTokenPayload.sub || idTokenPayload['cognito:username'];
      const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

      localStorage.setItem(`${keyPrefix}.LastAuthUser`, lastAuthUser);
      localStorage.setItem(`${keyPrefix}.${lastAuthUser}.idToken`, tokens.id_token);
      localStorage.setItem(`${keyPrefix}.${lastAuthUser}.accessToken`, tokens.access_token);
      localStorage.setItem(`${keyPrefix}.${lastAuthUser}.refreshToken`, tokens.refresh_token);
      localStorage.setItem(`${keyPrefix}.${lastAuthUser}.clockDrift`, '0');

      console.log('✅ Tokens stored in localStorage for Amplify');

      // Now Amplify should be able to find the authenticated user
      const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const session = await Auth.currentSession();

      console.log('✅ Amplify session established');

      // Update ToS and userType attributes if needed
      if (oauthState.tosData) {
        try {
          await Auth.updateUserAttributes(currentUser, {
            'custom:tosAcceptedVersion': oauthState.tosData.version,
            'custom:tosAcceptedAt': oauthState.tosData.acceptedAt,
            'custom:marketingConsent': oauthState.tosData.marketingConsent.toString(),
          });
          console.log('✅ ToS data updated');
        } catch (attrError) {
          console.error('⚠️ Failed to update ToS attributes:', attrError);
        }
      }

      if (oauthState.userType) {
        try {
          await Auth.updateUserAttributes(currentUser, {
            'profile': oauthState.userType,
          });
          console.log('✅ UserType updated');
        } catch (attrError) {
          console.error('⚠️ Failed to update userType attribute:', attrError);
        }
      }

      // Get user attributes
      let attributesMap: { [key: string]: string } = {};
      let userType: UserType | null = oauthState.userType || null;
      let userName = '';

      // First try the ID token claims — always available, no extra scope required
      if (idTokenPayload.email) attributesMap['email'] = idTokenPayload.email;
      if (idTokenPayload.name) attributesMap['name'] = idTokenPayload.name;
      if (idTokenPayload.profile && isValidUserType(idTokenPayload.profile)) {
        userType = idTokenPayload.profile;
      }
      userName = idTokenPayload.name || idTokenPayload.email?.split('@')[0] || '';

      // Then try to enrich via Auth.userAttributes (may fail if the access token
      // lacks the `aws.cognito.signin.user.admin` scope — non-fatal).
      try {
        const attributes = await Auth.userAttributes(currentUser);
        if (Array.isArray(attributes)) {
          attributes.forEach((attr: any) => {
            if (attr.Name && attr.Value) {
              attributesMap[attr.Name] = attr.Value;
            }
          });
        }

        const userTypeFromProfile = attributesMap['profile'];
        if (isValidUserType(userTypeFromProfile)) {
          userType = userTypeFromProfile;
        }

        userName = attributesMap['name'] || attributesMap['email']?.split('@')[0] || userName;
      } catch (attrError) {
        console.log('⚠️ Failed to fetch user attributes (using ID token claims):', attrError);
        userName = idTokenPayload.name || idTokenPayload.email?.split('@')[0] || '';
      }

      const userId = currentUser.username || currentUser.sub || lastAuthUser;

      // Fetch profile ID based on userType
      let hostId: string | undefined;
      let influencerId: string | undefined;

      try {
        if (userType && isHostLike(userType)) {
          const hostProfileResponse = await graphqlApi.getHostProfileByUserId(userId);
          if (hostProfileResponse.success && hostProfileResponse.data) {
            hostId = hostProfileResponse.data.hostId;
          }
        } else if (userType === USER_TYPES.INFLUENCER) {
          const influencerProfileResponse = await graphqlApi.getInfluencerByUserId(userId);
          if (influencerProfileResponse.success && influencerProfileResponse.data) {
            influencerId = influencerProfileResponse.data.influencerId;
          }
        }
      } catch (profileError) {
        console.log('⚠️ Failed to fetch profile ID:', profileError);
      }

      const redirectPath = oauthState.redirectPath;
      clearOAuthState();

      const tokenClaims = parseClaimsFromPayload(idTokenPayload as Record<string, unknown>);

      return {
        success: true,
        message: 'OAuth authentication successful!',
        data: {
          user: {
            userId,
            email: attributesMap['email'] || idTokenPayload.email || '',
            name: userName,
            userType,
            hostId,
            influencerId,
            roles: tokenClaims.roles,
            permissions: tokenClaims.permissions,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          tokens: {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
          },
          isSignedIn: true,
          redirectPath,
        },
      };
    } catch (error: any) {
      console.error('❌ OAuth callback handling failed:', error);
      clearOAuthState();
      return {
        success: false,
        error: error.message || 'OAuth authentication failed',
      };
    }
  },

  /**
   * Handles Naver OAuth callback — exchanges code directly with Naver API,
   * then finds/creates user in Cognito via backend GraphQL mutation
   */
  async handleNaverCallback(code: string, oauthState: any): Promise<ApiResponse> {
    try {
      console.log('🚀 Handling Naver OAuth callback...');

      const redirectUri = `${window.location.origin}/oauth/callback`;
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

      if (!graphqlEndpoint) {
        clearOAuthState();
        return { success: false, error: 'GraphQL endpoint not configured' };
      }

      // Call backend to handle Naver token exchange and user creation (avoids CORS)
      const naverOAuthMutation = `
        mutation NaverOAuth($input: NaverOAuthInput!) {
          naverOAuth(input: $input) {
            success
            message
            error
            data {
              userSub
            }
          }
        }
      `;

      console.log('🔄 Calling backend for Naver token exchange...');

      const gqlResponse = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_GRAPHQL_API_KEY || 'da2-yirsmocw3fcdfkm35omcpgtley',
        },
        body: JSON.stringify({
          query: naverOAuthMutation,
          variables: {
            input: {
              code,
              redirectUri
            },
          },
        }),
      });

      const gqlData = await gqlResponse.json();
      console.log('📋 Backend response:', gqlData);

      const result = gqlData?.data?.naverOAuth;

      if (!result?.success) {
        clearOAuthState();
        return {
          success: false,
          error: result?.error || gqlData?.errors?.[0]?.message || 'Naver authentication failed',
        };
      }

      // Parse user info from message (JSON encoded)
      let naverUserInfo = { email: '', emailSynthetic: false, naverId: '', name: '', picture: null as string | null, userType: null as string | null, idToken: '', accessToken: '', refreshToken: '' };
      try {
        naverUserInfo = JSON.parse(result.message);
      } catch (e) {
        // message might not be JSON
      }

      const userId = result.data?.userSub || null;
      const userName = naverUserInfo.name || naverUserInfo.email?.split('@')[0] || '';
      // Use userType from backend (DynamoDB) first, then from OAuth state
      const userType = naverUserInfo.userType || oauthState.userType || null;
      const redirectPath = oauthState.redirectPath;

      // For NEW Naver users (no userType means no DynamoDB record yet) we
      // deliberately withhold the Cognito tokens from Amplify's localStorage
      // until they've finished /complete-registration. Otherwise Amplify
      // sees a live session and the app treats them as signed in before
      // the registration form is submitted. The tokens are still returned
      // in the response payload so CompleteOAuthRegistration can promote
      // them to localStorage once registration succeeds.
      const isExistingUser = !!userType;

      let tokens: { accessToken?: string; idToken?: string; refreshToken?: string } = {};
      if (naverUserInfo.idToken && naverUserInfo.accessToken) {
        tokens = {
          accessToken: naverUserInfo.accessToken,
          idToken: naverUserInfo.idToken,
          ...(naverUserInfo.refreshToken ? { refreshToken: naverUserInfo.refreshToken } : {}),
        };

        if (isExistingUser) {
          const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';
          let lastAuthUser = userId || '';
          try {
            const idTokenPayload = JSON.parse(atob(naverUserInfo.idToken.split('.')[1]));
            lastAuthUser = idTokenPayload.sub || idTokenPayload['cognito:username'] || userId || '';
          } catch (e) {
            console.log('⚠️ Failed to parse ID token payload');
          }

          const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
          localStorage.setItem(`${keyPrefix}.LastAuthUser`, lastAuthUser);
          localStorage.setItem(`${keyPrefix}.${lastAuthUser}.idToken`, naverUserInfo.idToken);
          localStorage.setItem(`${keyPrefix}.${lastAuthUser}.accessToken`, naverUserInfo.accessToken);
          if (naverUserInfo.refreshToken) {
            localStorage.setItem(`${keyPrefix}.${lastAuthUser}.refreshToken`, naverUserInfo.refreshToken);
          }
          localStorage.setItem(`${keyPrefix}.${lastAuthUser}.clockDrift`, '0');

          console.log('✅ Naver Cognito tokens stored in localStorage for Amplify');
        } else {
          console.log('⏳ New Naver user — holding tokens until complete-registration succeeds');
        }
      } else {
        console.log('⚠️ No Cognito tokens returned from backend for Naver user');
      }

      clearOAuthState();

      const naverTokenClaims = parseClaimsFromIdTokenString(naverUserInfo.idToken);

      return {
        success: true,
        message: 'Naver authentication successful!',
        data: {
          user: {
            userId,
            email: naverUserInfo.email,
            // Tell the UI whether the email is a synthetic placeholder
            // (naver_<id>@naver.local) so the complete-registration page
            // can prompt the user to enter a real email instead of
            // showing the placeholder as read-only.
            emailSynthetic: !!naverUserInfo.emailSynthetic,
            oauthProvider: 'naver',
            naverId: naverUserInfo.naverId,
            name: userName,
            userType,
            profileImage: naverUserInfo.picture,
            roles: naverTokenClaims.roles,
            permissions: naverTokenClaims.permissions,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          tokens,
          isSignedIn: true,
          redirectPath,
        },
      };
    } catch (error: any) {
      console.error('❌ Naver OAuth callback failed:', error);
      clearOAuthState();
      return {
        success: false,
        error: error.message || 'Naver authentication failed',
      };
    }
  },

  /**
   * Completes OAuth sign-up by updating user attributes with ToS and userType data
   * This should be called after OAuth authentication is complete
   */
  async completeOAuthSignUp(): Promise<ApiResponse> {
    try {
      console.log('🚀 Completing OAuth sign-up...');

      // Retrieve OAuth state data
      const oauthState = retrieveOAuthState();
      if (!oauthState) {
        console.log('⚠️ No OAuth state found - user may have signed in directly');
        return {
          success: true,
          message: 'No additional setup needed',
        };
      }

      // If ToS data exists, update user attributes
      if (oauthState.tosData) {
        console.log('📝 Updating user attributes with ToS data...');
        try {
          await this.updateUserAttributes({
            'custom:tosAcceptedVersion': oauthState.tosData.version,
            'custom:tosAcceptedAt': oauthState.tosData.acceptedAt,
            'custom:marketingConsent': oauthState.tosData.marketingConsent.toString(),
          });
          console.log('✅ ToS data updated');
        } catch (attrError) {
          console.error('⚠️ Failed to update ToS attributes:', attrError);
          // Continue anyway - ToS can be updated later
        }
      }

      // If userType exists, update user attributes
      if (oauthState.userType) {
        console.log('📝 Updating user attributes with userType...');
        try {
          await this.updateUserAttributes({
            'profile': oauthState.userType,
          });
          console.log('✅ UserType updated');
        } catch (attrError) {
          console.error('⚠️ Failed to update userType attribute:', attrError);
          // Continue anyway
        }
      }

      // Clear OAuth state
      clearOAuthState();

      return {
        success: true,
        message: 'OAuth sign-up completed successfully',
        data: {
          redirectPath: oauthState.redirectPath,
        },
      };
    } catch (error: any) {
      console.error('❌ OAuth sign-up completion failed:', error);
      clearOAuthState();
      return {
        success: false,
        error: error.message || 'Failed to complete OAuth sign-up',
      };
    }
  },

  /**
   * Initiates a password reset by asking Cognito to email the user a verification code.
   *
   * Security note (Requirement 2.4 — no user enumeration): when Cognito reports
   * `UserNotFoundException`, we intentionally return `success: true` with the
   * same generic confirmation message used for registered emails. Callers must
   * rely on this contract and display an identical message regardless of
   * whether the email is registered.
   */
  async forgotPassword(email: string): Promise<ApiResponse> {
    try {
      console.log('🚀 Initiating password reset...', { email });

      await Auth.forgotPassword(email);

      console.log('✅ Password reset code sent');

      return {
        success: true,
        // Callers should render user-facing text via i18n using errorCode /
        // a success key; `message` is kept only for legacy callers.
        message: 'RESET_CODE_SENT',
        errorCode: 'RESET_CODE_SENT',
      };
    } catch (error: any) {
      console.error('❌ ForgotPassword error:', error);

      // Requirement 2.4: suppress UserNotFoundException so callers cannot
      // distinguish registered from unregistered emails.
      if (error.code === 'UserNotFoundException') {
        return {
          success: true,
          message: 'RESET_CODE_SENT',
          errorCode: 'RESET_CODE_SENT',
        };
      }

      if (error.code === 'LimitExceededException') {
        return {
          success: false,
          errorCode: 'LIMIT_EXCEEDED',
          error: error.message,
        };
      }

      if (error.code === 'InvalidParameterException') {
        return {
          success: false,
          errorCode: 'INVALID_EMAIL',
          error: error.message,
        };
      }

      return {
        success: false,
        errorCode: 'FORGOT_PASSWORD_FAILED',
        error: error.message,
      };
    }
  },

  /**
   * Completes the password reset by submitting the verification code and new
   * password to Cognito's `forgotPasswordSubmit` API.
   */
  async resetPassword(email: string, code: string, newPassword: string): Promise<ApiResponse> {
    try {
      console.log('🚀 Submitting password reset...', { email });

      await Auth.forgotPasswordSubmit(email, code, newPassword);

      console.log('✅ Password reset successful');

      return {
        success: true,
        message: 'PASSWORD_RESET_SUCCESS',
        errorCode: 'PASSWORD_RESET_SUCCESS',
      };
    } catch (error: any) {
      console.error('❌ ResetPassword error:', error);

      if (error.code === 'CodeMismatchException') {
        return {
          success: false,
          errorCode: 'CODE_MISMATCH',
          error: error.message,
        };
      }

      if (error.code === 'ExpiredCodeException') {
        return {
          success: false,
          errorCode: 'CODE_EXPIRED',
          error: error.message,
        };
      }

      if (error.code === 'LimitExceededException') {
        return {
          success: false,
          errorCode: 'LIMIT_EXCEEDED',
          error: error.message,
        };
      }

      if (error.code === 'InvalidPasswordException') {
        return {
          success: false,
          errorCode: 'INVALID_PASSWORD',
          error: error.message,
        };
      }

      // UserNotFoundException here is unexpected (the code was issued for a
      // valid email), but surface it as a generic failure so we still avoid
      // confirming account existence on this endpoint.
      if (error.code === 'UserNotFoundException') {
        return {
          success: false,
          errorCode: 'RESET_REQUEST_NEW_CODE',
          error: error.message,
        };
      }

      return {
        success: false,
        errorCode: 'RESET_PASSWORD_FAILED',
        error: error.message,
      };
    }
  },

  /**
   * Updates Cognito user attributes
   * @param attributes Key-value pairs of attributes to update
   */
  async updateUserAttributes(attributes: Record<string, string>): Promise<void> {
    try {
      console.log('📝 Updating user attributes...', Object.keys(attributes));

      const currentUser = await Auth.currentAuthenticatedUser();
      
      // Convert attributes to Cognito format
      const cognitoAttributes = Object.entries(attributes).reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

      await Auth.updateUserAttributes(currentUser, cognitoAttributes);

      console.log('✅ User attributes updated successfully');
    } catch (error: any) {
      console.error('❌ Failed to update user attributes:', error);
      throw error;
    }
  },
};
